import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import Separator from '../../../../components/simple/Separator';
import SocialLogin from '../../components/SocialLogin';

import PromoCodeAlert from '@components/Checkout/PromoCodeAlert';
import { Button, EmailInput } from '@components/Form';
import { Icon } from '@components/icons';
import { Agreements } from 'src/app/[locale]/(default)/[id]/components/atoms/Agreements';

const schema = z.object({
  email: z.string().email(),
  agreementDE: z.undefined(),
  marketingAgreement: z.boolean().refine((value) => value === true),
  commercialInside: z.boolean().refine((value) => value === true),
  commercialOutside: z.boolean().optional(),
});

interface RegisterWithEmailFormProps {
  className?: string;
  onSubmit: (data: any, setError: (...data: any) => void) => Promise<void>;
}

export default function RegisterWithEmailFormV2({ className, onSubmit }: RegisterWithEmailFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const t = useTranslations('auth');

  const onSubmitForm = handleSubmit(async (data) => {
    await onSubmit(data, setError);
  });

  return (
    <form className={className} onSubmit={onSubmitForm}>
      <div className="flex flex-col items-center justify-center">
        <Icon icon="star-hollow" color={'#F28B2D'} size={18} className="lg:my-4" />
      </div>
      <h1 className="mb-3 text-center text-xl font-bold text-premium-dark lg:text-2xl dark:text-white">
        {t('register.title')}
      </h1>
      <h2 className="text-md mb-3 text-center text-premium-dark lg:mb-4 lg:text-lg dark:text-white">
        {t('register.registerWithEmail')}
      </h2>
      <div className="mb-2 lg:mb-3">
        <EmailInput
          containerClass="mb-1"
          placeholder={t('register.email')}
          error={!!errors.email}
          {...register('email')}
        />
        {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
      </div>
      <div className="px-4 py-2">
        <Agreements secondRequired={true} setValue={setValue} watch={watch} register={register} errors={errors} />
        <PromoCodeAlert suggestPromoCode={false} className="mb-2" />
      </div>

      <div className="flex flex-col items-center justify-center">
        <Button className="mb-4 lg:mb-8" disabled={isSubmitting} loading={isSubmitting}>
          {t('register.submit')}
        </Button>
      </div>

      <Separator className="mb-4 lg:mb-8">{t('label.or')}</Separator>
      <div className="m-auto mb-4 flex flex-col items-center justify-center gap-2 lg:mb-8 lg:flex-row">
        <SocialLogin size="small" provider="transid" label={t('socialLogin.transID')} />
        <SocialLogin size="small" provider="linkedin" label={t('socialLogin.linkedIn')} />
      </div>
    </form>
  );
}
